import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@/config/serverConfig';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { projectsPathForDesign } from '@/lib/designChoice';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import {
  parseAuthSessionBody,
  resolveDefaultHomePath,
  type AuthSessionResponseBody,
} from '@/types/access';
import '@/styles/index.css';
import '@/styles/auth.css';

type Step = 'email' | 'password' | 'create_password';

function webLoginDeviceMeta() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return {
    platform: 'web' as const,
    device_name: ua ? ua.slice(0, 80) : 'Web',
  };
}

export function AuthPage() {
  const { setSession, isAuthenticated, loading: authLoading, capabilities } = useAuth();
  const { showError } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSetupToken, setPasswordSetupToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      showError('Введите почту');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, password: '', ...webLoginDeviceMeta() }),
      });
      const data = await res.json();

      if (res.status === 429) {
        showError(data.error || 'Слишком много попыток. Попробуйте позже.');
        return;
      }

      if (data.requirePasswordSetup && typeof data.setupToken === 'string' && data.setupToken) {
        setPasswordSetupToken(data.setupToken);
        setStep('create_password');
      } else if (data.needPassword) {
        setStep('password');
      } else if (data.success && data.user) {
        const session = parseAuthSessionBody(data as AuthSessionResponseBody);
        if (!session) {
          showError('Ошибка входа');
          return;
        }
        setSession(session);
        navigate(resolveDefaultHomePath(session.capabilities, projectsPathForDesign()));
      } else {
        showError(data.error || 'Ошибка входа');
      }
    } catch {
      showError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      showError('Введите пароль');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password, ...webLoginDeviceMeta() }),
      });
      const data = await res.json();

      if (res.status === 429) {
        showError(data.error || 'Слишком много попыток. Попробуйте позже.');
        return;
      }

      if (data.requirePasswordSetup && typeof data.setupToken === 'string' && data.setupToken) {
        setPasswordSetupToken(data.setupToken);
        setPassword('');
        setConfirmPassword('');
        setStep('create_password');
        return;
      }

      if (data.success && data.user) {
        const session = parseAuthSessionBody(data as AuthSessionResponseBody);
        if (!session) {
          showError(data.error || 'Неверные учётные данные');
          return;
        }
        setSession(session);
        navigate(resolveDefaultHomePath(session.capabilities, projectsPathForDesign()));
      } else {
        showError(data.error || 'Неверные учётные данные');
      }
    } catch {
      showError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      showError('Пароль должен содержать минимум 6 символов');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      showError('Пароль должен содержать заглавную букву');
      return;
    }
    if (!/[a-z]/.test(password)) {
      showError('Пароль должен содержать строчную букву');
      return;
    }
    if (!/[0-9]/.test(password)) {
      showError('Пароль должен содержать цифру');
      return;
    }
    if (password !== confirmPassword) {
      showError('Пароли не совпадают');
      return;
    }
    if (!passwordSetupToken) {
      showError('Ошибка сессии');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/set-initial-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setupToken: passwordSetupToken,
          password,
          confirmPassword,
          ...webLoginDeviceMeta(),
        }),
      });
      const data = await res.json();

      if (res.status === 429) {
        showError(data.error || 'Слишком много попыток. Попробуйте позже.');
        return;
      }

      if (data.success && data.user) {
        const session = parseAuthSessionBody(data as AuthSessionResponseBody);
        if (!session) {
          showError(data.error || 'Ошибка установки пароля');
          return;
        }
        setSession(session);
        navigate(resolveDefaultHomePath(session.capabilities, projectsPathForDesign()));
      } else {
        showError(data.error || 'Ошибка установки пароля');
      }
    } catch {
      showError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setPasswordSetupToken(null);
  };

  if (authLoading) {
    return <div className="app-loading">Загрузка…</div>;
  }

  if (isAuthenticated) {
    return <Navigate to={resolveDefaultHomePath(capabilities, projectsPathForDesign())} replace />;
  }

  return (
    <div className="auth-page">
      <div className="auth-page__card">
        <header className="auth-page__header">
          <h1 className="auth-page__title">Трекер проектов</h1>
          <p className="auth-page__subtitle">Вход в систему</p>
        </header>

        {step === 'email' && (
          <form onSubmit={handleEmailSubmit} className="auth-page__form" autoComplete="on" method="post">
            <Input
              type="email"
              name="username"
              inputMode="email"
              autoComplete="username"
              placeholder="name@company.ru"
              aria-label="Почта"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              disabled={loading}
              fullWidth
            />
            <Button type="submit" disabled={loading} fullWidth size="l">
              {loading ? 'Проверка…' : 'Далее'}
            </Button>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="auth-page__form" autoComplete="on" method="post">
            <Input
              type="email"
              name="username"
              autoComplete="username"
              placeholder="name@company.ru"
              aria-label="Почта"
              value={email}
              readOnly
              disabled={loading}
              fullWidth
            />
            <Input
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Пароль"
              aria-label="Пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              disabled={loading}
              fullWidth
            />
            <div className="auth-page__actions">
              <Button type="button" variant="secondary" onClick={handleBack} disabled={loading} fullWidth size="l">
                Назад
              </Button>
              <Button type="submit" disabled={loading} fullWidth size="l">
                {loading ? 'Вход…' : 'Войти'}
              </Button>
            </div>
          </form>
        )}

        {step === 'create_password' && (
          <form onSubmit={handleCreatePasswordSubmit} className="auth-page__form" autoComplete="on" method="post">
            <p className="auth-page__hint">Установите пароль для первого входа</p>
            <Input
              type="email"
              name="username"
              autoComplete="username"
              placeholder="name@company.ru"
              aria-label="Почта"
              value={email}
              readOnly
              disabled={loading}
              fullWidth
            />
            <Input
              type="password"
              name="new-password"
              autoComplete="new-password"
              placeholder="Новый пароль"
              aria-label="Новый пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              disabled={loading}
              fullWidth
            />
            <Input
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              placeholder="Подтвердите пароль"
              aria-label="Подтверждение пароля"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              fullWidth
            />
            <p className="auth-page__reqs">
              Минимум 6 символов, заглавная, строчная буква, цифра
            </p>
            <div className="auth-page__actions">
              <Button type="button" variant="secondary" onClick={handleBack} disabled={loading} fullWidth size="l">
                Назад
              </Button>
              <Button type="submit" disabled={loading} fullWidth size="l">
                {loading ? 'Сохранение…' : 'Установить и войти'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
